// Room outline queries (CIN-15126, phase 0). Pure; relative imports only so node --test loads it.
// Conventions (orientation, edge ids, bulge): see types.ts.
//
// DECISIONS
// - Outlines are immutable values. Everything expensive (edge list, tessellation) is cached per outline
//   OBJECT identity in a WeakMap; editing code must build a new object, never mutate vertices.
// - Point and distance queries are EXACT on arcs (no tessellation). `tessellate` is for drawing, clipping
//   and validation; `scanlineIntervals` uses a fine 2 mm tessellation (inscribed, so at most 2 mm short).
// - `pointInOutline` treats the boundary as inside within `eps` mm (distance based).
// - `footprintInsideOutline` = all corners inside (with `tol` slack) AND no edge of the footprint, shrunk by
//   `tol`, touches an outline edge. For a simple polygon that is sufficient, and it catches a reflex corner
//   poking into a footprint whose corners are all inside.
// - `distanceOutside` measures the footprint's corners only (0 when all are inside).
// - `clampInside` pushes an outside/too-close point to the nearest wall point plus `inset` along the wall's
//   local inward normal. Near a reflex vertex that can land marginally short of the inset; good enough for
//   dragging and camera clamps, not a proof.

import { arcOf, edgeLength, tessellateEdge } from './arc.ts';
import type { Arc } from './arc.ts';
import type { OutlineEdge, OutlineFootprint, OutlineLike, PlanPoint, PlanRect, RoomOutline } from './types.ts';

const TWO_PI = Math.PI * 2;
const EPS = 1e-9;

const edgeCache = new WeakMap<object, readonly OutlineEdge[]>();

/** The outline's edges in vertex order; edge i runs from vertex i to vertex i + 1. Cached, do not mutate. */
export function edgesOf(o: OutlineLike): readonly OutlineEdge[] {
    const cached = edgeCache.get(o);
    if (cached) {
        return cached;
    }
    const { vertices } = o;
    const edges = vertices.map((v, index): OutlineEdge => {
        const w = vertices[(index + 1) % vertices.length];
        const chord = Math.hypot(w.x - v.x, w.y - v.y);
        const bulge = v.bulge ?? 0;
        return {
            id: v.id,
            index,
            a: { x: v.x, y: v.y },
            b: { x: w.x, y: w.y },
            bulge,
            chord,
            length: edgeLength(v, w, bulge),
            isScreen: o.screenEdge !== undefined && v.id === o.screenEdge,
            normal: chord > EPS ? { x: -(w.y - v.y) / chord, y: (w.x - v.x) / chord } : { x: 0, y: 0 }
        };
    });
    edgeCache.set(o, edges);
    return edges;
}

export type Tessellation = {
    /** The ring without a repeated closing point; point i starts a segment toward point i + 1. */
    points: PlanPoint[];
    /** `edgeOf[i]` = id of the outline edge the segment starting at point i belongs to. */
    edgeOf: string[];
};

const tessellationCache = new WeakMap<object, Map<number, Tessellation>>();

/** The outline as a polygon: straight edges stay one segment, arcs are cut to within `tol` mm. Cached per outline object and tolerance. */
export function tessellate(o: OutlineLike, tol = 20): Tessellation {
    let byTol = tessellationCache.get(o);
    const cached = byTol?.get(tol);
    if (cached) {
        return cached;
    }
    const points: PlanPoint[] = [];
    const edgeOf: string[] = [];
    for (const edge of edgesOf(o)) {
        const run = tessellateEdge(edge.a, edge.b, edge.bulge, tol);
        for (let i = 0; i < run.length - 1; i += 1) {
            points.push(run[i]);
            edgeOf.push(edge.id);
        }
    }
    const result = { points, edgeOf };
    if (!byTol) {
        byTol = new Map();
        tessellationCache.set(o, byTol);
    }
    byTol.set(tol, result);
    return result;
}

/** Exact signed area in mm2: positive for counter-clockwise; arcs contribute their circular segment. */
export function signedArea(o: OutlineLike): number {
    let twice = 0;
    let segments = 0;
    for (const { a, b, bulge } of edgesOf(o)) {
        twice += a.x * b.y - b.x * a.y;
        const arc = arcOf(a, b, bulge);
        if (arc) {
            segments += ((arc.radius * arc.radius) / 2) * (arc.sweep - Math.sin(arc.sweep));
        }
    }
    return twice / 2 + segments;
}

/**
 * The same outline, counter-clockwise. Reversing keeps every arc where it is (the bulge moves to the
 * reversed edge and flips sign) and re-points `screenEdge` at the reversed screen edge's new start vertex.
 * Returns the input object when it already is counter-clockwise.
 */
export function ensureCcw<T extends OutlineLike>(o: T): T {
    if (signedArea(o) >= 0) {
        return o;
    }
    const n = o.vertices.length;
    const vertices = o.vertices.map((_, k) => {
        const v = o.vertices[n - 1 - k];
        const bulge = o.vertices[(n - 2 - k + n) % n].bulge;
        return bulge ? { ...v, bulge: -bulge } : { id: v.id, x: v.x, y: v.y };
    });
    let screenEdge = o.screenEdge;
    if (screenEdge !== undefined) {
        const index = o.vertices.findIndex((v) => v.id === screenEdge);
        if (index >= 0) {
            screenEdge = o.vertices[(index + 1) % n].id;
        }
    }
    return { ...o, vertices, ...(screenEdge !== undefined ? { screenEdge } : {}) };
}

/** Exact bounding box, arc extremes included. */
export function outlineBounds(o: OutlineLike): PlanRect {
    const rect: PlanRect = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    const grow = (x: number, y: number) => {
        rect.minX = Math.min(rect.minX, x);
        rect.maxX = Math.max(rect.maxX, x);
        rect.minY = Math.min(rect.minY, y);
        rect.maxY = Math.max(rect.maxY, y);
    };
    for (const { a, b, bulge } of edgesOf(o)) {
        grow(a.x, a.y);
        const arc = arcOf(a, b, bulge);
        if (arc) {
            for (const axis of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
                if (angleWithinArc(arc, axis)) {
                    grow(arc.center.x + arc.radius * Math.cos(axis), arc.center.y + arc.radius * Math.sin(axis));
                }
            }
        }
    }
    return rect;
}

/**
 * The legacy `width`/`depth` box of an outline: symmetric about the screen-wall center, so
 * width = 2 * max(|minX|, |maxX|) and depth = -minY. Rounded UP to whole mm so the box contains the room.
 */
export function symmetricSize(o: OutlineLike): { width: number; depth: number } {
    const { minX, maxX, minY } = outlineBounds(o);
    return {
        width: Math.ceil(2 * Math.max(Math.abs(minX), Math.abs(maxX)) - 1e-6),
        depth: Math.ceil(-minY - 1e-6)
    };
}

/** Counter-clockwise angle of `angle` past the arc start, in [0, 2PI), for a positive sweep; mirrored for a negative one. */
function arcParameter(arc: Arc, angle: number): number {
    const delta = arc.sweep >= 0 ? angle - arc.startAngle : arc.startAngle - angle;
    return ((delta % TWO_PI) + TWO_PI) % TWO_PI;
}

function angleWithinArc(arc: Arc, angle: number): boolean {
    return arcParameter(arc, angle) <= Math.abs(arc.sweep) + EPS;
}

type WallHit = { edgeId: string; point: PlanPoint; distance: number; normal: PlanPoint; s: number };

/** Local inward normal of an edge at `point` (which lies on it). */
function inwardNormalAt(edge: OutlineEdge, arc: Arc | null, point: PlanPoint): PlanPoint {
    if (!arc) {
        return edge.normal;
    }
    const dx = (arc.center.x - point.x) / arc.radius;
    const dy = (arc.center.y - point.y) / arc.radius;
    // A positive bulge bows outward: the center is on the interior side. A negative one is the mirror.
    return edge.bulge > 0 ? { x: dx, y: dy } : { x: -dx, y: -dy };
}

function nearestOnEdge(edge: OutlineEdge, p: PlanPoint): WallHit {
    const arc = arcOf(edge.a, edge.b, edge.bulge);
    let point: PlanPoint;
    let s: number;
    if (!arc) {
        const dx = edge.b.x - edge.a.x;
        const dy = edge.b.y - edge.a.y;
        const t =
            edge.chord > EPS
                ? Math.min(1, Math.max(0, ((p.x - edge.a.x) * dx + (p.y - edge.a.y) * dy) / (edge.chord * edge.chord)))
                : 0;
        point = { x: edge.a.x + dx * t, y: edge.a.y + dy * t };
        s = t * edge.chord;
    } else {
        const angle = Math.atan2(p.y - arc.center.y, p.x - arc.center.x);
        const rel = arcParameter(arc, angle);
        if (rel <= Math.abs(arc.sweep) && Math.hypot(p.x - arc.center.x, p.y - arc.center.y) > EPS) {
            point = { x: arc.center.x + arc.radius * Math.cos(angle), y: arc.center.y + arc.radius * Math.sin(angle) };
            s = rel * arc.radius;
        } else {
            const toA = Math.hypot(p.x - edge.a.x, p.y - edge.a.y);
            const toB = Math.hypot(p.x - edge.b.x, p.y - edge.b.y);
            const atA = toA <= toB;
            point = atA ? edge.a : edge.b;
            s = atA ? 0 : edge.length;
        }
    }
    return {
        edgeId: edge.id,
        point: { x: point.x, y: point.y },
        distance: Math.hypot(p.x - point.x, p.y - point.y),
        normal: inwardNormalAt(edge, arc, point),
        s
    };
}

/** The closest point on the boundary: which edge, where, how far, the wall's inward normal there, and mm along the edge. */
export function nearestWall(o: OutlineLike, p: PlanPoint): WallHit {
    let best: WallHit | null = null;
    for (const edge of edgesOf(o)) {
        const hit = nearestOnEdge(edge, p);
        if (!best || hit.distance < best.distance) {
            best = hit;
        }
    }
    if (!best) {
        throw new Error('nearestWall: outline has no edges');
    }
    return best;
}

/** Whether `p` is inside the circular segment cut off by a curved edge (on the arc's bulge side of the chord). */
function inArcSegment(edge: OutlineEdge, arc: Arc, p: PlanPoint): boolean {
    if (Math.hypot(p.x - arc.center.x, p.y - arc.center.y) > arc.radius) {
        return false;
    }
    const side = (edge.b.x - edge.a.x) * (p.y - edge.a.y) - (edge.b.y - edge.a.y) * (p.x - edge.a.x);
    return edge.bulge > 0 ? side < 0 : side > 0;
}

/**
 * Inside the outline, boundary included within `eps` mm. Exact on arcs: an even-odd test on the chord
 * polygon, then the circular segments of outward arcs are added and those of inward arcs removed.
 */
export function pointInOutline(o: OutlineLike, p: PlanPoint, eps = 0.5): boolean {
    const edges = edgesOf(o);
    for (const edge of edges) {
        if (nearestOnEdge(edge, p).distance <= eps) {
            return true;
        }
    }
    let inside = false;
    for (const { a, b } of edges) {
        if (a.y > p.y !== b.y > p.y && p.x < a.x + ((p.y - a.y) * (b.x - a.x)) / (b.y - a.y)) {
            inside = !inside;
        }
    }
    for (const edge of edges) {
        if (!edge.bulge) continue;
        const arc = arcOf(edge.a, edge.b, edge.bulge);
        if (arc && inArcSegment(edge, arc, p)) {
            return edge.bulge > 0;
        }
    }
    return inside;
}

/** Whether segment p-q meets the edge (a straight or curved outline edge); touching counts. */
function segmentTouchesEdge(p: PlanPoint, q: PlanPoint, edge: OutlineEdge): boolean {
    const arc = arcOf(edge.a, edge.b, edge.bulge);
    if (!arc) {
        return segmentsIntersect(p, q, edge.a, edge.b);
    }
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < EPS) {
        return false;
    }
    const fx = p.x - arc.center.x;
    const fy = p.y - arc.center.y;
    const b = fx * dx + fy * dy;
    const c = fx * fx + fy * fy - arc.radius * arc.radius;
    const disc = b * b - len2 * c;
    if (disc < 0) {
        return false;
    }
    const root = Math.sqrt(disc);
    return [(-b - root) / len2, (-b + root) / len2].some((t) => {
        if (t < 0 || t > 1) return false;
        const x = p.x + dx * t;
        const y = p.y + dy * t;
        return angleWithinArc(arc, Math.atan2(y - arc.center.y, x - arc.center.x));
    });
}

const cross = (o: PlanPoint, a: PlanPoint, b: PlanPoint): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

/** Whether segments p1-p2 and p3-p4 share any point (touching and collinear overlap included). */
export function segmentsIntersect(p1: PlanPoint, p2: PlanPoint, p3: PlanPoint, p4: PlanPoint): boolean {
    const d1 = cross(p3, p4, p1);
    const d2 = cross(p3, p4, p2);
    const d3 = cross(p1, p2, p3);
    const d4 = cross(p1, p2, p4);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
        return true;
    }
    const onSegment = (a: PlanPoint, b: PlanPoint, p: PlanPoint) =>
        Math.min(a.x, b.x) - EPS <= p.x &&
        p.x <= Math.max(a.x, b.x) + EPS &&
        Math.min(a.y, b.y) - EPS <= p.y &&
        p.y <= Math.max(a.y, b.y) + EPS;
    return (
        (Math.abs(d1) < EPS && onSegment(p3, p4, p1)) ||
        (Math.abs(d2) < EPS && onSegment(p3, p4, p2)) ||
        (Math.abs(d3) < EPS && onSegment(p1, p2, p3)) ||
        (Math.abs(d4) < EPS && onSegment(p1, p2, p4))
    );
}

/** The four corners of a footprint, in the same order as the studio's `footprintCorners`. */
export function footprintCornersOf(f: OutlineFootprint, shrink = 0): PlanPoint[] {
    const hw = Math.max(0, f.width / 2 - shrink);
    const hd = Math.max(0, f.depth / 2 - shrink);
    const rad = (f.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return (
        [
            [-hw, -hd],
            [hw, -hd],
            [hw, hd],
            [-hw, hd]
        ] as const
    ).map(([lx, ly]) => ({ x: f.x + lx * cos + ly * sin, y: f.y - lx * sin + ly * cos }));
}

/** Whether the whole footprint lies inside the outline (see the module header for the rule). */
export function footprintInsideOutline(o: OutlineLike, f: OutlineFootprint, tol = 5): boolean {
    if (!footprintCornersOf(f).every((c) => pointInOutline(o, c, tol))) {
        return false;
    }
    const inner = footprintCornersOf(f, tol);
    const edges = edgesOf(o);
    for (let i = 0; i < 4; i += 1) {
        const p = inner[i];
        const q = inner[(i + 1) % 4];
        if (edges.some((edge) => segmentTouchesEdge(p, q, edge))) {
            return false;
        }
    }
    return true;
}

/** How far the footprint pokes out of the outline, mm: the largest corner distance to the boundary; 0 when every corner is inside. */
export function distanceOutside(o: OutlineLike, f: OutlineFootprint): number {
    let worst = 0;
    for (const corner of footprintCornersOf(f)) {
        if (!pointInOutline(o, corner, 0)) {
            worst = Math.max(worst, nearestWall(o, corner).distance);
        }
    }
    return worst;
}

/** Distance along `dir` from `origin` to the boundary (first hit ahead), or null. `dir` need not be unit length. */
export function rayToOutline(o: OutlineLike, origin: PlanPoint, dir: PlanPoint): number | null {
    const length = Math.hypot(dir.x, dir.y);
    if (length < EPS) {
        return null;
    }
    const d = { x: dir.x / length, y: dir.y / length };
    let best: number | null = null;
    const consider = (t: number) => {
        if (t > 1e-7 && (best === null || t < best)) {
            best = t;
        }
    };
    for (const edge of edgesOf(o)) {
        const arc = arcOf(edge.a, edge.b, edge.bulge);
        if (!arc) {
            const ex = edge.b.x - edge.a.x;
            const ey = edge.b.y - edge.a.y;
            const denom = d.x * ey - d.y * ex;
            if (Math.abs(denom) < EPS) continue;
            const wx = edge.a.x - origin.x;
            const wy = edge.a.y - origin.y;
            const t = (wx * ey - wy * ex) / denom;
            const u = (wx * d.y - wy * d.x) / denom;
            if (u >= -EPS && u <= 1 + EPS) consider(t);
        } else {
            const fx = origin.x - arc.center.x;
            const fy = origin.y - arc.center.y;
            const b = fx * d.x + fy * d.y;
            const c = fx * fx + fy * fy - arc.radius * arc.radius;
            const disc = b * b - c;
            if (disc < 0) continue;
            const root = Math.sqrt(disc);
            for (const t of [-b - root, -b + root]) {
                if (angleWithinArc(arc, Math.atan2(fy + d.y * t, fx + d.x * t))) consider(t);
            }
        }
    }
    return best;
}

/**
 * The x intervals of the outline along the horizontal line at `y`, left to right (even-odd). An L through
 * its notch gives one, a U or an alcove hall gives two. Uses a 2 mm tessellation.
 */
export function scanlineIntervals(o: OutlineLike, y: number): [number, number][] {
    const { points } = tessellate(o, 2);
    const xs: number[] = [];
    for (let i = 0; i < points.length; i += 1) {
        const p = points[i];
        const q = points[(i + 1) % points.length];
        if (p.y <= y !== q.y <= y) {
            xs.push(p.x + ((y - p.y) * (q.x - p.x)) / (q.y - p.y));
        }
    }
    xs.sort((a, b) => a - b);
    const intervals: [number, number][] = [];
    for (let i = 0; i + 1 < xs.length; i += 2) {
        if (xs[i + 1] - xs[i] > EPS) {
            intervals.push([xs[i], xs[i + 1]]);
        }
    }
    return intervals;
}

/** `p` if it is inside and at least `inset` mm from every wall, else the nearest wall point pushed `inset` inward. */
export function clampInside(o: OutlineLike, p: PlanPoint, inset = 0): PlanPoint {
    const wall = nearestWall(o, p);
    const inside = pointInOutline(o, p, 0);
    if (inside && wall.distance >= inset) {
        return p;
    }
    return { x: wall.point.x + wall.normal.x * inset, y: wall.point.y + wall.normal.y * inset };
}

const fmt = (n: number): string => String(Math.round(n * 100) / 100 + 0);

/**
 * SVG path data of the outline with `A` commands for arcs: one source for the plan, print and thumbnails.
 * `flipY` negates plan y (plan y is up, SVG y is down) and flips the arc sweep flag with it.
 */
export function outlinePathD(o: OutlineLike, flipY: boolean): string {
    const edges = edgesOf(o);
    if (edges.length === 0) {
        return '';
    }
    const ty = (y: number) => fmt(flipY ? -y : y);
    let d = `M${fmt(edges[0].a.x)} ${ty(edges[0].a.y)}`;
    for (const edge of edges) {
        const closing = edge.index === edges.length - 1;
        const arc = arcOf(edge.a, edge.b, edge.bulge);
        if (arc) {
            // sweep-flag 1 = increasing angle in SVG user space; flipping y reverses the angle direction.
            const sweepFlag = edge.bulge > 0 !== flipY ? 1 : 0;
            d += `A${fmt(arc.radius)} ${fmt(arc.radius)} 0 0 ${sweepFlag} ${fmt(edge.b.x)} ${ty(edge.b.y)}`;
        } else if (!closing) {
            // The closing straight edge is implied by Z.
            d += `L${fmt(edge.b.x)} ${ty(edge.b.y)}`;
        }
    }
    return `${d}Z`;
}

const rectangleCache = new Map<string, RoomOutline>();
const RECTANGLE_CACHE_LIMIT = 64;

/**
 * The implicit rectangle of a `width` x `depth` room: r1 (+hw, 0) -> r2 (-hw, 0) -> r3 (-hw, -d) -> r4 (+hw, -d),
 * screen edge 'r1'. Equal sizes return the SAME object, so tessellation caches hit across room objects. The
 * vertices are whole mm (half the width and the depth rounded up), so an odd width is at most 1 mm wider.
 */
export function rectangleOutline(width: number, depth: number): RoomOutline {
    const key = `${width}x${depth}`;
    const cached = rectangleCache.get(key);
    if (cached) {
        return cached;
    }
    // Whole-mm vertices, rounded up like `symmetricSize`: an odd width or a fractional depth would otherwise make
    // every shape edit of a plain rectangle fail validation (`bad-vertex`: coordinates are integers).
    const hw = Math.ceil(width / 2);
    const back = Math.ceil(depth);
    const outline: RoomOutline = {
        screenEdge: 'r1',
        vertices: [
            { id: 'r1', x: hw, y: 0 },
            { id: 'r2', x: -hw, y: 0 },
            { id: 'r3', x: -hw, y: -back },
            { id: 'r4', x: hw, y: -back }
        ]
    };
    if (rectangleCache.size >= RECTANGLE_CACHE_LIMIT) {
        rectangleCache.clear();
    }
    rectangleCache.set(key, outline);
    return outline;
}
